import { useState, useCallback } from 'react';
import { LTSSService } from '../types/ltss';
import { parseExcelFile } from '../utils/parseExcel';

interface UseFileParserReturn {
  services: LTSSService[];
  isLoading: boolean;
  error: string | null;
  fileName: string | null;
  parseFile: (file: File) => Promise<void>;
  clearData: () => void;
  loadServices: (services: LTSSService[], fileName: string) => void;
}

export function useFileParser(): UseFileParserReturn {
  const [services, setServices] = useState<LTSSService[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const parseFile = useCallback(async (file: File) => {
    setIsLoading(true);
    setError(null);
    setFileName(file.name);

    try {
      const parsedServices = await parseExcelFile(file);
      setServices(parsedServices);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse file');
      setServices([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearData = useCallback(() => {
    setServices([]);
    setFileName(null);
    setError(null);
  }, []);

  const loadServices = useCallback((loadedServices: LTSSService[], loadedFileName: string) => {
    setServices(loadedServices);
    setFileName(loadedFileName);
    setError(null);
  }, []);

  return {
    services,
    isLoading,
    error,
    fileName,
    parseFile,
    clearData,
    loadServices,
  };
}
